/* eslint-disable react/jsx-no-bind */
// @flow
import React from 'react';
import { xor, intersectionWith, isEqual, union } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';

import { AsyncMultiSelectFilter } from 'common/components/core/select';

import OrderActions from 'common/redux/actions/orderMaster';
import OrderMasterSelectors from 'common/redux/selectors/orderMaster';
import { clientSelector } from 'common/redux/selectors/queryParamsSelectors';
import type { MasterOrder } from 'common/models/internal/MasterOrder';

type Props = {
    value: ?{ allowEmptyOrNull: boolean, values: Array<string> },
    onChange: (?{ values: Array<string>, allowEmptyOrNull: boolean }) => void
};

export const OrderFilter = ({
    value: appliedValues,
    onChange,
    ...rest
}: Props) => {
    const match = useRouteMatch();
    const clientId = useSelector(state => clientSelector(state, { match }));
    const ordersData = useSelector(state =>
        OrderMasterSelectors.getOrderFilterData(state, { match })
    );
    const orders = ordersData ? ordersData.orders : [];
    const dispatch = useDispatch();
    const getOptionId = (option: MasterOrder): string => option.id;

    const fetchFilteredOrders = React.useCallback(
        (filters = []) => {
            dispatch(OrderActions.fetchOrders({ clientId }, { filters }));
        },
        [dispatch, clientId]
    );

    //overriding onOptionSelect to deal with issues in ES-29935
    const onOptionSelect = React.useCallback(
        (selectedValue: Object | any, isClearAll?: boolean) => {
            const { allowEmptyOrNull, values } = selectedValue;
            const currentSelectedValues = values.map(value =>
                getOptionId(value)
            );

            //filtering out unselected options from applied filters (ES-29715)
            const currentPageOrders = orders.map(value => getOptionId(value));
            const diffValues = xor(
                currentPageOrders,
                appliedValues?.values || []
            );
            const newFilters = intersectionWith(
                diffValues,
                appliedValues?.values,
                isEqual
            );

            const allSelectedValues = [...newFilters, ...currentSelectedValues];
            if (!allSelectedValues.length || isClearAll) {
                onChange && onChange();
                return;
            }
            onChange({
                values: union(allSelectedValues),
                allowEmptyOrNull
            });
        },
        [onChange, appliedValues, orders]
    );
    return (
        <AsyncMultiSelectFilter
            {...rest}
            options={orders}
            value={appliedValues}
            getOptionValue={getOptionId}
            getOptionLabel={getOptionId}
            fetchFilteredOptions={fetchFilteredOrders}
            filterOptionsBasedOn="id"
            onOptionSelect={onOptionSelect}
            onClear={onChange}
            disableBlanksSelectOption
        />
    );
};
